import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import Reveal from './Reveal';
import { SectionMarker } from './Gap';

export const faqs = [
  {
    q: 'What is Empax Studio?',
    a: 'Empax is an experience design and engineering studio based in Mumbai, India. We synthesise architecture, creativity and technology into spaces, events and immersive experiences, from the first idea to the final build.',
  },
  {
    q: 'What does “synthesis” mean at Empax?',
    a: 'Synthesis means one idea held by everyone who delivers it. Our architect, experience designer, creative and technology leads work in the same conversation from week one, so nothing gets lost in handovers between concept and build.',
  },
  {
    q: 'What services does Empax offer?',
    a: 'Six connected crafts: architecture and space, experience and UX, technology and AV (projection mapping, LED, spatial audio, lighting and show control), story and strategy, structural and civil engineering, and production and delivery.',
  },
  {
    q: 'What are immersive zones?',
    a: 'Immersive zones are dedicated spaces where projection, LED, sound, lighting and interactivity combine to surround a visitor in a single experience. We design and engineer them end to end, inside experience centres, exhibitions and events.',
  },
  {
    q: 'What technologies do you use?',
    a: 'Projection and projection mapping, LED walls and screens, spatial and immersive audio, dynamic lighting, media servers and show control, sensors, cameras and real-time engines. We combine them to build immersive, interactive and reactive spaces, and only bring in the technology that earns its place in the experience.',
  },
  {
    q: 'How do you make a space immersive and interactive?',
    a: 'We layer image, sound, light and space so a room surrounds you, then add interactivity and reactivity so it responds to you. Movement, touch, presence and sound can trigger the environment in real time, turning visitors from spectators into participants.',
  },
  {
    q: 'Do you design custom interactive and reactive installations?',
    a: 'Yes. We design and engineer bespoke interactive installations, from touch and gesture to sensor-driven and generative real-time content, built to run reliably on site and tuned to the story of the space.',
  },
  {
    q: 'Who does Empax work with?',
    a: 'Event agencies, wedding agencies, museum designers, experience centres, immersive zones, theatre, exhibitions and pavilions designers, and retail environment specialists.',
  },
  {
    q: 'Where is Empax based, and do you work outside Mumbai?',
    a: 'We are based in Mumbai and work across India and internationally, staying with a project through fabrication, installation and on-site handover.',
  },
  {
    q: 'How do we start a project with Empax?',
    a: 'Start a conversation through the contact form or email experience@empaxstudio.com. The first conversation is with the same team that will design and build the work.',
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" className="hidden relative section-pad border-t border-[var(--border)] bg-[var(--bg-2)]">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <Reveal><SectionMarker index="VIII" label="COMMON QUESTIONS" className="label-gap" /></Reveal>
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <Reveal>
              <h2 className="font-display font-medium text-[clamp(1.9rem,3.8vw,3.2rem)] leading-[1.06] tracking-[-0.01em]">
                Questions, <span className="font-serif-c italic text-[var(--accent)] font-normal">answered plainly.</span>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <div className="border-t border-[var(--border)]">
              {faqs.map((item, i) => {
                const isOpen = open === i;
                return (
                  <Reveal key={item.q} delay={i * 50}>
                    <div className="border-b border-[var(--border)]">
                      <button
                        type="button"
                        data-testid={`faq-toggle-${i}`}
                        aria-expanded={isOpen}
                        onClick={() => setOpen(isOpen ? -1 : i)}
                        className="w-full flex items-center justify-between gap-6 py-7 text-left group"
                      >
                        <span className={`font-display text-[1.15rem] md:text-[1.4rem] font-medium transition-colors ${isOpen ? 'text-[var(--accent)]' : 'text-[var(--text)] group-hover:text-[var(--accent)]'}`}>{item.q}</span>
                        <span className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center transition-all duration-500 ${isOpen ? 'border-[var(--accent)] bg-[var(--accent)] rotate-[135deg]' : 'border-[var(--border-strong)] group-hover:border-[var(--accent)]'}`}>
                          <Plus size={16} className={isOpen ? 'text-[#0A1030]' : 'text-[var(--muted)]'} />
                        </span>
                      </button>
                      <div className={`grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                        <div className="overflow-hidden">
                          <p className="text-[var(--muted)] leading-relaxed font-light max-w-[70ch] pb-7 pr-12">{item.a}</p>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
